import React, { useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BusinessInfoInput,
  CompletionStatus,
  CreditApplicationBusinessEntity,
  CreditApplicationBusinessOwnership,
  MajorAccountChange,
  SectionName,
} from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import { FormDateInput, FormDropdown, FormTextInput } from '@divvy-web/skylab.form'
import Icon, { ICON_SIZE_REGULAR } from '@divvy-web/skylab.icon'
import { DeepPartial, FormProvider, useForm, useFormState, useWatch } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'
import Tooltip, { TooltipPositions } from '@divvy-web/skylab.tooltip'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import MaskedFormTextInput from '@sputnik/components/MaskedFormTextInput'
import { getFormattedEIN, getFormattedSsn, limitInputLength } from '@sputnik/components/utils'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import CreditApplicationPageLayout from '@sputnik/pages/CreditApplicationPages/CreditApplicationPageLayout'
import FormAutoSubmitProvider from '@sputnik/pages/CreditApplicationPages/FormAutoSubmit'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { hasMissingRequiredField } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { numberOfEmployees } from '@sputnik/schema/creditApplication/businessInfo'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { removeNonNumericCharacters } from '@sputnik/utils/dataUtils'

import EntityTypeCombobox from './EntityTypeCombobox'
import { useGetNameAndOwnershipDataLazyQuery } from './GetNameAndOwnershipData.gql'
import IndustryCombobox from './IndustryCombobox'
import NameAndOwnershipFormMarketingContent from './NameAndOwnershipFormMarketingContent'
import {
  type NameAndOwnershipFormValidationSchema,
  nameAndOwnershipFormValidationSchema,
} from './nameAndOwnershipFormValidationSchema'
import styles from './styles.module.scss'
import { useUpdateNameAndOwnershipData } from './UpdateNameAndOwnershipData.gql'

const NameAndOwnershipForm = () => {
  const [getClassName] = useNameFormatter('NameAndOwnershipForm')
  const { appId } = useParams()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const showToast = useToast()
  // query to see if we have values here
  const { isMajorAccountChange, hasMajorAccountChange } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  /* tracking start */

  const { tracker } = useWebTracking()

  const completionStatus = useApplicationProgress({ sectionName: SectionName.BUSINESS_INFO })

  useEffect(() => {
    tracker?.track('se_signup__application_business_info_name__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  const [getNameAndOwnershipData, { error }] = useGetNameAndOwnershipDataLazyQuery({
    variables: { creditApplicationId: appId ?? '' },
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load business name and ownership data.'
          id='sputnik.NameAndOwnershipForm__pc3EKe'
        />
      ),
    },
  })

  const [updateNameAndOwnershipData] = useUpdateNameAndOwnershipData({
    onError: () => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error updating business info'
          id='sputnik.NameAndOwnershipForm__pzR4M6'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const methods = useForm<NameAndOwnershipFormValidationSchema>({
    mode: 'all',
    defaultValues: async () => {
      const { data } = await getNameAndOwnershipData()

      const {
        legalBusinessName,
        preferredAccountName,
        website,
        ownership,
        formedOn,
        entityType,
        naicsCode,
        taxIdLastFour,
        numberOfEmployees,
        industry,
      } = data?.creditApplication?.businessInfo ?? {}

      return {
        legalBusinessName: legalBusinessName ?? '',
        preferredAccountName: preferredAccountName ?? '',
        website: website ?? '',
        taxId: {
          value: '',
          display: taxIdLastFour
            ? entityType === CreditApplicationBusinessEntity.SOLE_PROPRIETORSHIP
              ? getFormattedSsn(`*****${taxIdLastFour}`, true)
              : getFormattedEIN(`*****${taxIdLastFour}`, true)
            : '',
        },
        ownership,
        formedOn,
        numberOfEmployees,
        naicsCode,
        entityType,
        industry,
      }
    },
    resolver: yupResolver(nameAndOwnershipFormValidationSchema),
  })

  const { control, setValue, reset } = methods
  const { isLoading } = useFormState({ control })

  const saveNameAndOwnershipData = useCallback(
    async (data: DeepPartial<NameAndOwnershipFormValidationSchema>) => {
      const { taxId, ...rest } = data
      const input: BusinessInfoInput = {
        ...rest,
        ...(data.numberOfEmployees !== undefined &&
          data.numberOfEmployees !== null && {
            numberOfEmployees: numberOfEmployees.cast(data.numberOfEmployees),
          }),
        ...(data.taxId?.value ? { taxId: data.taxId.value } : { taxId: undefined }),
      }
      await updateNameAndOwnershipData({
        variables: {
          creditApplicationId: appId ?? '',
          businessInfo: input,
        },
        onCompleted: (updatedData) => {
          // tracking for name and ownership section and business info section as a whole
          const businessInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
            (section) =>
              section?.sectionName === SectionName.BUSINESS_INFO &&
              section?.completionStatus === CompletionStatus.COMPLETED,
          )

          if (businessInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
            tracker?.track('se_signup__application_business_info_section_progress__completed', {
              is_complete: true,
            })
          }

          const hasMissingInfo = hasMissingRequiredField([
            updatedData?.updateCreditApplication?.businessInfo?.legalBusinessName,
            updatedData?.updateCreditApplication?.businessInfo?.ownership,
            updatedData?.updateCreditApplication?.businessInfo?.formedOn,
            updatedData?.updateCreditApplication?.businessInfo?.taxIdLastFour,
            updatedData?.updateCreditApplication?.businessInfo?.entityType,
            updatedData?.updateCreditApplication?.businessInfo?.numberOfEmployees,
            updatedData?.updateCreditApplication?.businessInfo?.naicsCode,
            updatedData?.updateCreditApplication?.businessInfo?.industry,
          ])

          if (!hasMissingInfo) {
            tracker?.track('se_signup__application_business_info_name_section_progress__completed', {
              is_complete: true,
            })
          }

          // logic for sending individual field tracking
          const {
            legalBusinessName,
            preferredAccountName,
            website,
            ownership,
            formedOn,
            entityType,
            naicsCode,
            taxId,
            numberOfEmployees,
          } = data

          if (taxId) {
            tracker?.track('se_signup__application_business_info_name_tax_id__submitted', { value_present: true })
          }

          if (numberOfEmployees) {
            tracker?.track('se_signup__application_business_info_name_employees__submitted', { value_present: true })
          }

          if (legalBusinessName) {
            tracker?.track('se_signup__application_business_info_name_legal_name__submitted', { value_present: true })
          }

          if (preferredAccountName) {
            tracker?.track('se_signup__application_business_info_name_preferred_name__submitted', {
              value_present: true,
            })
          }

          if (website) {
            tracker?.track('se_signup__application_business_info_name_business_website__submitted', {
              value_present: true,
            })
          }

          if (ownership) {
            tracker?.track('se_signup__application_business_info_name_ownership__submitted', { value_present: true })
          }

          if (formedOn) {
            tracker?.track('se_signup__application_business_info_name_formation_date__submitted', {
              value_present: true,
            })
          }

          if (entityType) {
            tracker?.track('se_signup__application_business_info_name_entity_type__submitted', { value_present: true })
          }

          if (naicsCode) {
            tracker?.track('se_signup__application_business_info_name_industry__submitted', { value_present: true })
          }

          reset(data)
        },
      })
    },
    [appId, completionStatus, reset, tracker, updateNameAndOwnershipData],
  )

  const placeholder = (
    <FormattedMessage
      defaultMessage='Select'
      id='sputnik.NameAndOwnershipForm__kQAf2d'
    />
  )

  const optionalText = (
    <FormattedMessage
      defaultMessage='Optional'
      id='sputnik.NameAndOwnershipForm__InWqys'
    />
  )

  const entityType = useWatch({ control, name: 'entityType' })
  const isSoleProprietorship = entityType === CreditApplicationBusinessEntity.SOLE_PROPRIETORSHIP

  const onNextClick = async () => {
    tracker?.track('se_signup__application_business_info_name_next__fired')
    navigate(`../${PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <FormProvider {...methods}>
        <div className={styles[getClassName('form')]}>
          <FormAutoSubmitProvider updateHandler={saveNameAndOwnershipData} />
          <CreditApplicationPageLayout
            hideBackButton
            marketing={<NameAndOwnershipFormMarketingContent />}
            pageTitle={
              <FormattedMessage
                defaultMessage='Tell us about your business'
                id='sputnik.NameAndOwnershipForm__nameAndOwnership'
              />
            }
            onNextClick={onNextClick}
          >
            <div className={styles[getClassName('grid')]}>
              <FormTextInput
                control={control}
                disabled={isLoading}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Legal business name'
                      id='sputnik.NameAndOwnershipForm__U/+zt/'
                    />
                  </span>
                }
                name='legalBusinessName'
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.LEGAL_BUSINESS_NAME)}
              />
              <FormTextInput
                caption={optionalText}
                control={control}
                disabled={isLoading}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Preferred business name'
                      id='sputnik.NameAndOwnershipForm__Hq6+4+'
                    />
                  </span>
                }
                name='preferredAccountName'
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.PREFERRED_ACCOUNT_NAME)}
              />
              <FormTextInput
                caption={optionalText}
                control={control}
                disabled={isLoading}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Business website'
                      id='sputnik.NameAndOwnershipForm__go2n+4'
                    />
                  </span>
                }
                name='website'
                readOnly={isMajorAccountChange}
              />
              <MaskedFormTextInput
                className='fs-mask'
                control={control}
                disabled={isLoading}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Business tax ID / EIN'
                      id='sputnik.NameAndOwnershipForm__sgg/zz'
                    />
                  </span>
                }
                maskedValue={(value) =>
                  isSoleProprietorship ? getFormattedSsn(value, true) : getFormattedEIN(value, true)
                }
                maxLength={isSoleProprietorship ? 11 : 10}
                name='taxId'
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.TAX_ID)}
                sanitize={removeNonNumericCharacters}
                unmaskedValue={(value) => (isSoleProprietorship ? getFormattedSsn(value) : getFormattedEIN(value))}
              />
              <FormDropdown
                control={control}
                disabled={isLoading}
                isSearchable={false}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Ownership'
                      id='sputnik.NameAndOwnershipForm__wkn5Ny'
                    />
                  </span>
                }
                name='ownership'
                options={[
                  {
                    label: formatMessage({
                      defaultMessage: 'Public',
                      id: 'sputnik.NameAndOwnershipForm__uhu5aG',
                    }),
                    value: CreditApplicationBusinessOwnership.PUBLIC,
                  },
                  {
                    label: formatMessage({
                      defaultMessage: 'Private',
                      id: 'sputnik.NameAndOwnershipForm__viXE32',
                    }),
                    value: CreditApplicationBusinessOwnership.PRIVATE,
                  },
                ]}
                placeholder={placeholder}
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.OWNERSHIP)}
              />
              <FormDateInput
                shouldRenderCalendar
                addCharactersAtPositions={{
                  2: '/',
                  5: '/',
                }}
                control={control}
                disabled={isLoading}
                formatOptions={{
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }}
                label={
                  <>
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Formation date'
                        id='sputnik.NameAndOwnershipForm__MHV8J9'
                      />

                      <Tooltip
                        html={
                          <FormattedMessage
                            defaultMessage='This is the date your business was created.'
                            id='sputnik.NameAndOwnershipForm__auKHuS'
                          />
                        }
                        position={TooltipPositions.TOOLTIP_POSITION_RIGHT_START}
                      >
                        <Icon
                          name='info'
                          size={ICON_SIZE_REGULAR}
                        />
                      </Tooltip>
                    </span>
                  </>
                }
                maxLength={10}
                name='formedOn'
                placeholderGuide='MM/DD/YYYY'
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.FORMED_ON)}
                onChange={(_, date) => {
                  if (!date) {
                    return
                  }
                  setValue('formedOn', new Date(date), { shouldValidate: true, shouldDirty: true })
                }}
              />

              <FormTextInput
                control={control}
                disabled={isLoading}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Number of employees'
                      id='sputnik.NameAndOwnershipForm__cErP2u'
                    />
                  </span>
                }
                name='numberOfEmployees'
                readOnly={isMajorAccountChange}
                type='number'
                onInput={(e) => limitInputLength(e, 6)}
              />
              <IndustryCombobox
                control={control}
                disabled={isLoading}
                readOnly={
                  isMajorAccountChange &&
                  !hasMajorAccountChange(MajorAccountChange.INDUSTRY) &&
                  !hasMajorAccountChange(MajorAccountChange.NAICS_CODE)
                }
              />
              <EntityTypeCombobox
                control={control}
                readOnly={isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.ENTITY_TYPE)}
              />
            </div>
          </CreditApplicationPageLayout>
        </div>
      </FormProvider>
    </div>
  )
}

export default NameAndOwnershipForm
